<template>
  <POverlay v-if="modelValue" :overflow-hidden="true" @on-close="onClose">
    <div class="p-mobile-search-overlay">
      <div class="p-mobile-search-overlay__header">
        <p-search-input
          v-model="searchQuery"
          :placeholder="$t('general.search')"
          :hide-cancel="true"
          size="lg"
          @on-debounce="handleSearch"
          @on-clear="clearResults"
        />
        <span class="p-search-input--cancel" @click.stop="onClose" v-text="$t('general.abort')" />
      </div>

      <div v-if="showResults && searchResults.length > 0" class="p-mobile-search-overlay__results">
        <div
          v-for="(result, index) in searchResults"
          :key="index"
          class="p-advanced-search__result-row"
          @click="handleResultClick(result)"
        >
          <div class="p-advanced-search__result-title f-utility-12 t-fg-accent">
            {{ result.title }}
          </div>
          <div class="p-advanced-search__result-desc f-utility-14">
            <span v-html="highlightMatch(result.description)"></span>
          </div>
          <p-divider
            v-if="index !== searchResults.length - 1"
            style="width: 100%; margin-top: 12px"
          />
        </div>
      </div>
    </div>
  </POverlay>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'

// import { useSearchOverlay } from '@/composables/useSearchOverlay'
import { useSuggestionList } from '@/api/useSearch'

interface SearchResult {
  title: string
  description: string
  link: string
}

defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const router = useRouter()
const searchQuery = ref('')
const searchResults = ref<SearchResult[]>([])
const showResults = ref(false)
const { suggestionList, fetchSuggestionList } = useSuggestionList()

const handleSearch = () => {
  if (searchQuery.value.length < 2) {
    searchResults.value = []
    showResults.value = false
    return
  }

  searchResults.value = suggestionList.value.map((suggestion) => ({
    title: suggestion.tenant,
    description: suggestion.title,
    link: suggestion.url,
  }))

  showResults.value = true
}

const clearResults = () => {
  searchResults.value = []
  showResults.value = false
}

const handleResultClick = (result: SearchResult) => {
  router.push(result.link)
  onClose()
}

const onClose = () => {
  emit('update:modelValue', false)
  searchQuery.value = ''
  clearResults()
}

const highlightMatch = (text: string) => {
  if (!searchQuery.value || searchQuery.value.length < 2) {
    return `<span class="t-fg-primary">${text}</span>`
  }

  const searchTerm = escapeRegExp(searchQuery.value)
  const regex = new RegExp(searchTerm, 'gi')

  const parts = []
  let lastIndex = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        text: text.substring(lastIndex, match.index),
        isMatch: false,
      })
    }

    parts.push({
      text: match[0],
      isMatch: true,
    })

    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push({
      text: text.substring(lastIndex),
      isMatch: false,
    })
  }

  return parts
    .map((part) => {
      const className = part.isMatch ? 't-fg-accent' : 't-fg-primary'
      return `<span class="${className}">${part.text}</span>`
    })
    .join('')
}

const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

watch(searchQuery, async (newValue) => {
  if (newValue.length > 2) {
    await fetchSuggestionList(newValue)
    handleSearch()
  } else {
    clearResults()
  }
})
</script>
